import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import { useRef } from 'react'
import { motion, useScroll, useTransform } from 'motion/react'
import { aboutTeaserImage } from '@/constants/content'
import { TransitionLink } from '@/components/generals/page-transition'
import { usePrefersReducedMotion } from '@/hooks/use-media-query'

const SOFT_EASE = [0.22, 1, 0.36, 1] as const

/**
 * Full-bleed portrait behind the whole section, with two short centred
 * paragraphs and an "About me" link that slide in from the side.
 */
export const AboutBand: FC = (): ReactNode => {
    const ref = useRef<HTMLElement>(null)
    const still = usePrefersReducedMotion()
    const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] })
    const imageY = useTransform(scrollYProgress, [0, 1], ['-7%', '7%'])

    return (
        <section
            ref={ref}
            id='about-band'
            aria-label='About Aurelia'
            className='relative flex min-h-[100svh] items-center justify-center overflow-hidden py-28'
        >
            <div aria-hidden='true' className='absolute inset-0'>
                <motion.div
                    className='absolute inset-x-0 -inset-y-[8%]'
                    style={still ? undefined : { y: imageY }}
                >
                    <Image
                        src={aboutTeaserImage.src}
                        alt=''
                        fill
                        quality={85}
                        sizes='100vw'
                        className='object-cover object-[50%_35%]'
                    />
                </motion.div>

                <div className='absolute inset-0 bg-black/45' />
                <div className='absolute inset-0 bg-[radial-gradient(ellipse_55%_60%_at_center,rgba(0,0,0,0.55)_0%,rgba(0,0,0,0)_75%)]' />
                <div className='absolute inset-x-0 top-0 h-40 bg-linear-to-b from-background to-transparent' />
                <div className='absolute inset-x-0 bottom-0 h-40 bg-linear-to-t from-background to-transparent' />
            </div>

            <motion.div
                className='relative z-10 mx-auto flex max-w-[36rem] flex-col items-center px-6 text-center'
                initial={{ opacity: 0, x: -70 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.5 }}
                transition={{ duration: 1.4, ease: SOFT_EASE }}
            >
                <p className='text-[clamp(1.2rem,1.8vw,1.6rem)] font-light leading-[1.45] tracking-[-0.015em] text-white'>
                    I design the way I think: slowly at the start, precisely at the end. Every project begins with listening, turns into a strong idea, and ends with details most people feel without noticing.
                </p>

                <p className='mt-6 text-[clamp(1.2rem,1.8vw,1.6rem)] font-light leading-[1.45] tracking-[-0.015em] text-white'>
                    Step into my studio, where every detail is deliberate and your idea becomes the heart of the work.
                </p>

                <TransitionLink
                    href='/about'
                    className='mt-12 text-sm font-medium text-white underline decoration-white/50 underline-offset-[7px] transition-colors hover:text-gold hover:decoration-gold'
                >
                    About me
                </TransitionLink>
            </motion.div>
        </section>
    )
}
